/**
 * @module app.cm
 */

import { autoinject, observable, Disposable, ICollectionObserverSplice, BindingEngine } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { CaseService } from 'app/cm/services/case-service';
import { CarePlanGoalWithLocalState } from 'app/cm/models/local-state-extensions';
import { ITasksConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { getReference } from 'app/shared/models/essette-types';
import { IApiSettings, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { apiCall } from 'app/shared/lib/api/api';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import { ApiDate } from 'app/shared/models/essette-api';
import * as CM from 'app/cm/models/essette-api';;
import { localSort } from 'app/shared/lib/sorting';
import { allActionsSucceeded } from 'app/shared/lib/essette-api';
import { HouseholdCasePageContext } from 'app/cm/lib/household-case-page-context';
import { guid } from 'app/shared/lib/guid';

export type HasInterventionsFlag = { hasInterventions: boolean };
export type GoalWithLocalStateAndInterventionFlag = CarePlanGoalWithLocalState & HasInterventionsFlag;

export interface DuplicateGoal {
    memberDescription?: string;
    goalDescription?: string;
}

@autoinject()
export class AddGoals extends ComponentBase implements IForm {


    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ changeHandler: 'reload' })
    public householdCaseId: string | null = null;

    /** the value of the autocomplete textbox */
    @observable
    public goalSearchQuery: string | null = null;

    @bindable.objectNull
    protected taskConfig: ITasksConfig | null = null;

    protected goalSearchResults = new Array<CM.CarePlanGoalDefinition>();     // array of search results from API for autocomplete (not currently used)
    protected currentGoals: CarePlanGoalWithLocalState[] = [];
    protected goal: CM.CarePlanGoalDefinition | null = null;
    protected selectedGoals: CM.CarePlanGoalDefinition[] = [];
    protected goalLookups: CM.CarePlanGoalDefinition[] = [];
    protected priorityLookups: CM.CarePlanGoalPriority[] = [];
    @bindable.string
    protected goalOption: string = 'selectGoals';
    protected previousGoalOption: string = 'selectGoals';
    protected goalId: string | null = null;
    protected selectedGoalsSubscription: Disposable | null = null;
    protected insertGoals: GoalWithLocalStateAndInterventionFlag[] = [];
    protected customGoalText: string | null = null;
    protected caseSettings: IApiSettings = {};
    protected allowDuplicateGoals: boolean = false;
    /** Permission that controls whether a user can enter a custom goal */
    protected hasAddCustomGoals: boolean = false;
    /** Permission that controls whether a custom goal can be added to the library */
    protected hasAddCustomGoalToLibrary: boolean = false;
    protected problemId: string | null = null;
    protected customGoal: CM.CarePlanGoalDefinition | undefined = undefined;
    protected customIntervention: CM.CarePlanInterventionDefinition | undefined = undefined;
    /** Holds cases associated with a household case */
    protected cases: CM.Case[] = [];
    protected selectedCases: CM.Case[] = [];

    public step: number = 1;

    protected nextButtonEnabled: boolean = false;

    protected maxCustomDescriptionLength = 200;

    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly carePlanService: CarePlanService,
        private readonly bindingEngine: BindingEngine,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly context: HouseholdCasePageContext,
        private readonly caseService: CaseService,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!this.householdCaseId) { throw new Error('Bindable property [household-case-id] was not set'); }

        this.caseSettings = this.context.settings;
        this.checkPermissions();
        this.allowDuplicateGoals = !!getApiSetting(this.caseSettings, 'carePlan.goals.$allowDuplicates');

        this.reload();
    }

    protected attached(): void {
        this.observeSelectedGoals();
    }

    protected detached(): void {
        if(this.selectedGoalsSubscription) {
            this.selectedGoalsSubscription.dispose();
        }
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        if(!this.householdCaseId) { throw new Error('Bindable property [household-case-id] was not set'); }
        await this.loadLookups();
        await this.loadCurrentGoals(this.householdCaseId);
    }

    protected selectedGoalsChanged(splices: ICollectionObserverSplice<CM.CarePlanGoalDefinition>[]): void {
        if(this.insertGoals.length === 0 && this.selectedGoals.length === 0) return;

        for(let i = 0; i < splices.length; i++) {
            const splice = splices[i];

            const goalsAdded = this.selectedGoals.slice(splice.index, splice.index + splice.addedCount);
            if(goalsAdded.length > 0) {

                //console.log(`The following values were inserted at ${splice.index}: ${JSON.stringify(goalsAdded)}`);
                goalsAdded.forEach(
                    goal => {
                        const g: GoalWithLocalStateAndInterventionFlag = {
                            isCustom: false,
                            description: goal.description,
                            definition: { id: goal.id },
                            _state: { isSaved: false, addToLibrary: false },
                            hasInterventions: false
                        };
                        this.insertGoals.push(g);
                    });
            }

            if(splice.removed.length) {
                //console.log(`The following values were removed from ${splice.index}: ${JSON.stringify(splice.removed)}`);
                (splice.removed as CM.CarePlanGoalDefinition[]).forEach(
                    goal => {
                        let i = this.insertGoals.find(g => (g.definition && g.definition.id) === goal.id);
                        if(i) {
                            this.insertGoals.splice(this.insertGoals.indexOf(i), 1);
                        }
                    });
            }
        }
    }

    protected optionsChoiceChanged(): void {
        this.previousGoalOption = this.goalOption;
    }

    ////****************** Functions ************************************************////
    public get goalsCount(): number {
        return this.insertGoals.length;
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAddCustomGoals = this.accountContext.hasPermission('CM.CarePlanGoal.Insert.AddCustom');
        this.hasAddCustomGoalToLibrary = this.accountContext.hasPermission('CM.CarePlanGoal.Insert.AddToLibrary');
    }

    protected observeSelectedGoals(): void {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.selectedGoalsSubscription) {
            this.selectedGoalsSubscription.dispose();
        }
        this.selectedGoalsSubscription = this.bindingEngine
            .collectionObserver(this.selectedGoals) // observe the selectedGoals array, which changes in the multi-select goal definition Select element
            .subscribe(this.selectedGoalsChanged.bind(this)); // pass the splice information (addedCount, index, removed) to selectedGoalsChanged method

        // subscription disposed of in detached()
    }

    protected addAutoCompleteGoal(goal: CM.CarePlanGoalDefinition): void {
        this.selectedGoals.push(goal); // will trigger selectedGoalsChanged(), and add the goal to selectedGoals
        this.goalSearchQuery = '';

        this.goalSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
    }

    /** Called when a user types in a custom goal description and clicks the button */
    protected addCustomGoal(): void {
        const goal: GoalWithLocalStateAndInterventionFlag = {
            isCustom: true,
            description: this.customGoalText,
            _state: { isSaved: false, addToLibrary: false },
            hasInterventions: false
        };

        this.insertGoals.push(goal);

        this.customGoalText = null;
    }

    /** Called when a user removes a goal from the grid. Will ultimately trigger the selectedGoalsChanged() method */
    protected removeGoal(goal: GoalWithLocalStateAndInterventionFlag): void {
        if(goal.isCustom) {
            // Custom goals are not entries in selectedTasks, so no need to find and remove it
            const index = this.insertGoals.indexOf(goal);
            if(index >= 0) {
                this.insertGoals.splice(index, 1);
            }
        }
        else {
            // Find the goal in the selectedGoals array and remove it. This will trigger the selectedGoalsChanged(), which will update insertGoals
            const st = this.selectedGoals.find(t => t.id === (goal.definition && goal.definition.id));
            if(st) {
                this.selectedGoals.splice(this.selectedGoals.indexOf(st), 1);
            }
        }
    }

    protected filterGoals = (goal: CM.CarePlanGoalDefinition): boolean => {
        if(this.selectedGoals.some(g => goal.id === g.id) && this.goalOption === 'searchGoals') {
            return false;
        }
        if(!this.currentGoals && !goal) { return false; }
        return true;
    }

    protected copyPriority(priority: CM.CarePlanGoalPriority | null): void {
        this.insertGoals.forEach(g => {
            g.priority = priority;
        });
    }

    protected copyTargetDate(date: ApiDate | null): void {
        this.insertGoals.forEach(g => {
            g.targetDate = date;
        });
    }

    /** Takes a goal from the insertGoals grid and constructs a new copy to be sent to the API */
    protected buildSaveGoalWithCaseData(memberCase: CM.Case, goal: CarePlanGoalWithLocalState): CarePlanGoalWithLocalState {
        if(!goal._state) { goal._state = {}; }

        let saveGoal: CarePlanGoalWithLocalState = {
            case: getReference(memberCase)!,
            isCustom: goal.isCustom,
            priority: getReference(goal.priority) || undefined,
            targetDate: goal.targetDate,
            definition: (!goal.isCustom && getReference(goal.definition)) || undefined,
            description: (!goal.definition && goal.description) || (goal.externalSystem && goal.description) || undefined,
            externalSystem: goal.externalSystem,
            externalIdentifier: goal.externalIdentifier
        };

        if(this.hasAddCustomGoalToLibrary && !!goal._state.addToLibrary) {
            saveGoal.isCustom = false;
            delete saveGoal.definition;
            // Set addToLibrary to false to prevent cloning the goal while looping through member cases
            const originalGoal = this.insertGoals.find(ig => ig === goal);
            if(originalGoal?._state?.addToLibrary) {
                originalGoal._state.addToLibrary = false;
            }
        }

        return saveGoal;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        let success: boolean = true;
        success = success && await this.saveSelectedGoals();
        return success;
    }

    public reset(): void {
        this.selectedGoals.splice(0);
        this.insertGoals.splice(0);
        this.selectedCases.splice(0);

        if(this.householdCaseId) {
            this.loadCurrentGoals(this.householdCaseId);
        }

        this.problemId = null;

        this.goalSearchQuery = '';
        this.goalSearchResults.splice(0);
        this.goalOption = 'selectGoals';
        this.step = 1;
    }

    public async next(): Promise<void> {
        this.step = 2;
    }

    public async back(): Promise<void> {
        this.insertGoals = this.insertGoals.filter(g => !g.externalSystem);
        this.step = 1;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadLookups(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const caseIncludes: string[] = [
            'member'
        ]

        try {
            this.state.beginLoad();

            const [goalDefinitions, priorities, cases] = await Promise.all(
                [
                    this.carePlanService.loadGoalDefinitions(options, ['metadata.created']),
                    this.carePlanService.loadCarePlanGoalPriorities(options),
                    this.caseService.loadCasesByHouseholdCase(this.householdCaseId, options, caseIncludes)
                ]);

            this.goalLookups = goalDefinitions.results;
            this.priorityLookups = priorities.results;
            this.cases = cases.results;

            // use local sorting to sort cases by member description
            const caseSorting: Sorting = new Sorting();
            caseSorting.entries = [
                {
                    reference: 'member.description',
                    direction: SortDirection.Ascending
                }
            ];
            localSort(this.cases, caseSorting);

            this.customGoal = this.goalLookups.find(g => g.isCustom);

            let interventionDefinitionsResponse = await this.carePlanService.loadCustomInterventionDefinition();
            if(interventionDefinitionsResponse.isSuccessful && interventionDefinitionsResponse.results.length === 1) {
                this.customIntervention = interventionDefinitionsResponse.results[0];
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    /** Sends out batches of goals associated with case data or iterates individually for goals with interventions*/
    @apiCall
    private async saveSelectedGoals(): Promise<boolean> {
        const duplicateGoals: DuplicateGoal[] = [];
        const goalsForApi: CarePlanGoalWithLocalState[] = [];

        this.selectedCases.forEach(c => {
            this.insertGoals.filter(g => {
                // If user clicks save a second time, we skip goals that might have been saved already.
                return !(g._state && g._state.isSaved);
            }).forEach(g => {
                // Check for duplicate goals
                if(this.currentGoals.length > 0 && this.currentGoals.some(cg =>
                    ((cg.externalSystem?.code === 'CCI' && cg.externalIdentifier === g.externalIdentifier) ||
                        (cg.definition && cg.definition.id) === (g.definition && g.definition.id)) &&
                    (cg.member && cg.member.id) === (c.member && c.member.id))) {
                    duplicateGoals.push({ memberDescription: c.member?.description || '', goalDescription: g.description || '' });
                }

                goalsForApi.push(this.buildSaveGoalWithCaseData(c, g));
            })
        });

        /** Show confirmation to add duplicate goals */
        if(duplicateGoals.length > 0) {
            if(this.allowDuplicateGoals) {
                const message: string = this.i18n.tr('ui:messages.duplicate.confirm', { count: duplicateGoals.length, what: '$t(cm:carePlanGoal.$)' });
                const result = await confirmation.showCustom(message, duplicateGoals.map(dg => "Goal \"" + dg.goalDescription + "\" for Member \"" + dg.memberDescription + "\"").join('\n'), confirmation.options.StandardConfirm);
                if(result.dialogResponse === confirmation.options.CANCEL) {
                    //canceling means we just stop
                    return Promise.resolve(false);
                }
            }
            else {
                const message: string = this.i18n.tr('ui:messages.duplicate.message_error', { count: duplicateGoals.length, what: '$t(cm:carePlanGoal.$)' });
                await confirmation.showCustom(message, duplicateGoals.map(dg => "Goal \"" + dg.goalDescription + "\" for Member \"" + dg.memberDescription + "\"").join('\n'), confirmation.options.OK);
                return Promise.resolve(false);
            }
        }

        let success = false;
        try {
            this.state.beginSave();
            // Verify if goals are for external system and if interventions will need to be inserted as well.
            for(const g of goalsForApi.filter(goal => goal.externalSystem)) {
                if(this.problemId) g.problem = { id: this.problemId };
            }

            const batchGoalsForApi = goalsForApi.filter(goal => !goal.externalSystem);
            const progressToast = this.toaster.standard.batch('cm:carePlanGoal.$', batchGoalsForApi.length);
            progressToast.show();

            const responses = await this.carePlanService.insertCarePlanGoalsToCasesBatch(batchGoalsForApi, progressToast);
            success = allActionsSucceeded(responses);

            if(!success) {
                throw new Error('Goals did not save successfully');
            }

            return Promise.resolve(success);
        } catch(error) {
            this.toaster.standard.saved('cm:carePlanGoal.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }

    }

    @apiCall
    private async loadCurrentGoals(householdCaseId: string): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: false
        };

        const includes: string[] = ['externalSystem', 'problem', 'member'];

        try {
            this.state.beginLoad();
            const response = await this.carePlanService.loadGoalsByHouseholdCase(householdCaseId, options, includes);
            this.currentGoals = response.results;

        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    /** Called as user types into the auto-complete goal search box */
    @apiCall
    protected async searchGoals(value: string): Promise<void> {
        if(!value) {
            if(this.goalSearchResults) this.goalSearchResults = [];
            return;
        }

        const includes: string[] = ['metadata.created', 'metadata.delegated', 'metadata.completed.byUser'];
        try {
            this.state.beginLoad();
            const response = await this.carePlanService.searchGoalDefinitions(value, undefined, includes);
            this.goalSearchResults = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
